import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import { getTemplateUsage, getPromptTemplate } from '../services/api';

interface TemplateUsageDialogProps {
  open: boolean;
  templateId: string;
  onClose: () => void;
}

interface TemplateDetails {
  template_id: string;
  template_name: string;
  template_type: string;
  description: string;
  status: string;
}

interface MappingUsage {
  mapping_id: string;
  attribute_name: string;
  attribute_value: string;
  priority: number;
  assigned_at: string;
}

const TemplateUsageDialog: React.FC<TemplateUsageDialogProps> = ({ open, templateId, onClose }) => {
  const [template, setTemplate] = useState<TemplateDetails | null>(null);
  const [usages, setUsages] = useState<MappingUsage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && templateId) {
      loadTemplateUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId]);

  const loadTemplateUsage = async () => {
    setLoading(true);
    setError(null);
    try {
      const [templateData, usageData] = await Promise.all([
        getPromptTemplate(templateId),
        getTemplateUsage(templateId),
      ]);
      setTemplate(templateData);
      setUsages(usageData.mappings || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load template usage');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!template || !usages.length) return;

    const csv = [
      ['Attribute Name', 'Attribute Value', 'Priority', 'Assigned At'],
      ...usages.map((u) => [
        u.attribute_name,
        u.attribute_value,
        u.priority.toString(),
        new Date(u.assigned_at).toLocaleString(),
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${template.template_name}_usage_report.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Template Usage Report</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {template && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  {template.template_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <Chip label={template.template_type} size="small" color="primary" />
                  <Chip
                    label={template.status}
                    size="small"
                    color={template.status === 'active' ? 'success' : 'default'}
                  />
                </Box>
                {template.description && (
                  <Typography variant="body2" color="text.secondary">
                    {template.description}
                  </Typography>
                )}
              </Paper>
            )}

            {usages.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  This template is not currently assigned to any attribute mappings.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Attribute Mappings Using This Template ({usages.length}):
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Attribute Name</TableCell>
                        <TableCell>Attribute Value</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Assigned At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usages.map((usage) => (
                        <TableRow key={usage.mapping_id}>
                          <TableCell>
                            <Chip label={usage.attribute_name} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {usage.attribute_value}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={usage.priority} size="small" />
                          </TableCell>
                          <TableCell>{formatDate(usage.assigned_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {usages.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This template is currently in use by {usages.length} attribute mapping
                    {usages.length !== 1 ? 's' : ''}. Archiving it will affect these mappings.
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {usages.length > 0 && (
          <Button onClick={handleExport} disabled={loading}>
            Export CSV
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateUsageDialog;
